import api from "../../../services/api";
import { urlToBase64 } from "../../../utils/photoUtils"; // Assuming getCoordsFromLocationString exists or is added
import { toast } from "react-toastify";

// Function to delete a camera
export const deleteCameraApi = async (quotationId, pointId, cameraId) => {
  try {
    const token = localStorage.getItem("access_token");
    await api.delete(`/quotations/${quotationId}/point/${pointId}/cameras`, {
      headers: { Authorization: `Bearer ${token}` },
      data: { camera_id: cameraId }, // Include camera_id in the request body
    });
    // toast.success("La caméra a été supprimée avec succès");
    return true; // Indicate success
  } catch (error) {
    console.error("Error deleting camera:", error);
    toast.error("Impossible de supprimer la caméra");
    return false; // Indicate failure
  }
};

// Function to save point data (including photos and cameras)
export const savePointDataApi = async (
  dataToSave,
  quotationId,
  initialCameraIds
) => {
  try {
    const token = localStorage.getItem("access_token");

    // 1. Prepare and send PATCH request for point data (WITHOUT localisations)
    const pointPayload = {
      title: dataToSave.pointName,
      description: dataToSave.comment || "",
      type: dataToSave.pointType,
      support_type: dataToSave.supportType,
      connection_type: dataToSave.installationType,
      power_type: dataToSave.powerSupplyType,
      has_camera: dataToSave.hasCameras === "yes" ? 1 : 0,
      comment: dataToSave.comment || "",
      ...(dataToSave.pointType === "surveillance_center" && {
        recorder_size: dataToSave.recorderSize || "",
        screen_size: dataToSave.screenSize || "",
      }),
      ...(dataToSave.pointType === "high_point" && {
        coverage_area: dataToSave.coverageArea || "",
      }),
      // REMOVED: ...(apiLocalisations.length > 0 && { localisations: apiLocalisations }),
    };

    await api.patch(
      `/quotations/${quotationId}/point/${dataToSave.id}`,
      pointPayload,
      {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      }
    );

    const photosToProcess = dataToSave.photos?.filter(photo =>
        photo.isNew || // Always process new photos
        photo.file || // Process if the file was replaced
        (photo.id && photo.location !== photo.initialLocation) // Process if location changed for an existing photo
    ) || [];

    if (photosToProcess.length > 0) {
      const photoPromises = photosToProcess.map(async (photo) => {
        // Determine latitude and longitude
        let latitude = photo.latitude ?? 0;
        let longitude = photo.longitude ?? 0;

        // Use location string if available and valid
        if (typeof photo.location === "string") {
          const coords = getCoordsFromLocationString(photo.location);
          if (coords) {
            latitude = coords.latitude;
            longitude = coords.longitude;
          }
        } 
        // Fallback to EXIF data if location string didn't provide coords
        if (latitude === 0 && longitude === 0 && photo.exifData) {
            latitude = photo.exifData.latitude ?? 0;
            longitude = photo.exifData.longitude ?? 0;
        }

        // Get base64 string only if a new file object exists
        let base64String = null;
        if (photo.file) {
            const source = photo.file;
            base64String = await urlToBase64(source);
        }

        // Prepare payload base
        const localisationPayloadBase = {
          latitude: latitude,
          longitude: longitude,
        };

        if (photo.isNew) { 
          // *** POST is called ONLY if photo.isNew is true ***
          // Create new localisation - Requires base64
          if (!base64String) {
            console.warn("New photo is missing file data, skipping POST.");
            return Promise.resolve(); // Skip if no file data for new photo
          }
          const postPayload = {
            ...localisationPayloadBase,
            image_path: base64String // Send base64
          };
          return api.post(
            `/quotations/${quotationId}/point/${dataToSave.id}/localisation`,
            postPayload,
            { 
              headers: { 
                Authorization: `Bearer ${token}`, 
                'Content-Type': 'application/json' 
              } 
            }
          );
        } else if (photo.id && (photo.file || photo.location !== photo.initialLocation)) { 
          // *** PATCH is called ONLY if photo.isNew is false/undefined AND photo.id exists AND (file or location changed) ***
          // Update existing localisation
          // Determine image_path: Use new base64 if file changed, otherwise send null.
          const imagePathToSend = base64String ? base64String : null; // Send base64 if new file, else null

          const patchPayload = {
            ...localisationPayloadBase, // Contains latitude and longitude
            // Only include image_path if it's not null (i.e., if a new file was uploaded)
            ...(imagePathToSend !== null && { image_path: imagePathToSend })
          };
          return api.patch(
            `/quotations/${quotationId}/point/${dataToSave.id}/localisation/${photo.id}`,
            patchPayload,
            { 
              headers: { 
                Authorization: `Bearer ${token}`, 
                'Content-Type': 'application/json' 
              } 
            }
          );
        }
        return Promise.resolve(); // Should not happen with the filter, but good practice
      });
      await Promise.all(photoPromises);
      console.log("All necessary photos processed successfully.");
    } else {
        console.log("No new or modified photos to process.");
    }

    // 3. Process cameras (same logic as before)
    if (dataToSave.cameras && dataToSave.cameras.length > 0) {
      const cameraPromises = dataToSave.cameras.map((camera) => {
        const cameraPayload = {
          name: camera.name,
          role: camera.role,
          vision_area: camera.vision_area,
          comment: camera.comment || "",
        };

        if (camera.id && initialCameraIds.includes(camera.id)) {
          return api.patch(
            `/quotations/${quotationId}/point/${dataToSave.id}/cameras/${camera.id}`,
            cameraPayload,
            {
              headers: {
                Authorization: `Bearer ${token}`,
                "Content-Type": "application/json",
              },
            }
          );
        } else {
          // Create new camera
          return api.post(
            `/quotations/${quotationId}/point/${dataToSave.id}/cameras`,
            cameraPayload,
            {
              headers: {
                Authorization: `Bearer ${token}`,
                "Content-Type": "application/json",
              },
            }
          );
        }
      });
      await Promise.all(cameraPromises);
    }

    toast.success("Point, photos et caméras mis à jour avec succès");
  
    return true; // Indicate success
  } catch (error) {
    console.error(
      "Error updating point, photos, or cameras:",
      error.response ? error.response.data : error
    );
    let errorMessage = "Failed to update point, photos, or cameras.";
    if (error.response?.data?.message) {
      errorMessage += ` ${error.response.data.message}`;
    } else if (error.message) {
      errorMessage += ` ${error.message}`;
    }
    toast.error(errorMessage);
    return false; // Indicate failure
  }
};

// Helper function to parse location string (add this or import if it exists)
// Example implementation:
const getCoordsFromLocationString = (locationString) => {
  if (typeof locationString !== 'string') return null;
  const parts = locationString.split(',');
  if (parts.length === 2) {
    const lat = parseFloat(parts[0].trim());
    const lon = parseFloat(parts[1].trim());
    if (!isNaN(lat) && !isNaN(lon)) {
      return { latitude: lat, longitude: lon };
    }
  }
  return null;
};
